import { makeAutoObservable, runInAction, computed } from 'mobx';
import { Merchandise, PaymentChannel, Order } from './type';
import { createContext, useContext } from 'react';
import { getWithAuth } from '@/util';
import { mockData2, mockData4 } from './mockData';
import { ApplePayMethods } from '../constant';
import { vipObj, TimePeriods, CategoryToCurVip } from './constant';
import { filterPrice } from './utils';

// 使用utils中的Price类型，避免类型冲突
type FilteredPrice = ReturnType<typeof filterPrice>[0];

export class Store {
  merchandises: Merchandise[] = [];
  cardData: PaymentChannel[] = [];
  timeUnit: string = 'days';
  //   activity: Activity | undefined =   {
  //     "id": "1",
  //     "type": "userActivityTask",
  //     "taskType": "weeklyTopUp",
  //     "rewardWorth": 2, // 百分比，由客户端/web计算各档位返利数额，向下取整
  //     "beginTime": 1234567890000, // 开始时间，ms时间戳
  //     "endTime": 1836898828965, // 结束时间，ms时间戳
  //     "status": "pending"
  // };

  orderData: Order = {};
  loading: boolean = false;
  merchandiseStore: { [key: string]: Merchandise[] } = {};
  activeChannelIndex: number = 0;
  currentVip: number = 1;
  currentMonth: number = 1;
  source: string = '';
  canUp: boolean = false;
  initDone: boolean = false;
  offsetAmount: number = 0;
  orderId: string | undefined;
  countdown: string = '';
  countdownTimer: ReturnType<typeof setTimeout> | null = null;
  upgradeApiCache: { [key: string]: any } = {};
  showCashier: boolean = false;
  sessionData: any = {};
  cashierAmount: number = 0;
  backFromCashier: boolean = false;

  constructor() {
    makeAutoObservable(this, {
      // 声明computed属性
      activePayCard: computed,
      upIndexArr: computed,
      vipData: computed,
      promotionVipData: computed,
      upVipData: computed,
      upPrice: computed,
      priceSource: computed,
      curPayData: computed,
    });
  }

  get curPayData(): any {
    return this.priceSource?.filter(
      (item: any) =>
        item?.quantity === TimePeriods[this.currentMonth].value && item?.unit === TimePeriods[this.currentMonth].unit
    )[0];
  }

  get curProductId(): string {
    const { unit, value } = TimePeriods[this.currentMonth] || {};
    const curVip = CategoryToCurVip[this.currentVip];
    return this.merchandiseStore[curVip]?.find((item) => item.quantity === value && item.unit === unit)?.id || '';
  }

  // Computed: 当前选中的支付渠道
  get activePayCard(): PaymentChannel | undefined {
    return this.cardData[this.activeChannelIndex];
  }

  // Computed: 支持升级的支付渠道索引数组
  get upIndexArr(): number[] {
    return (
      this.cardData?.reduce((acc: number[], cur: PaymentChannel, index: number) => {
        if (cur?.enableUpgrade) {
          acc.push(index);
        }
        return acc;
      }, []) || []
    );
  }

  // Computed: 处理后的VIP数据
  get vipData(): FilteredPrice[] {
    return filterPrice(
      this.merchandiseStore[CategoryToCurVip[this.currentVip]],
      this.activePayCard?.tags as string[],
      false
    );
  }

  // Computed: 处理后的促销VIP数据
  get promotionVipData(): FilteredPrice[] {
    const rawData = this.merchandiseStore[CategoryToCurVip[this.currentVip]];
    const tags = this.activePayCard?.tags as string[];

    // 如果没有促销数据，返回空数组
    const hasPromotionData = rawData?.some(
      (item) => item.promotionStockKeepUnits?.length > 0 || item.discountStockKeepUnits?.length > 0
    );

    if (!hasPromotionData) {
      return [];
    }

    const data = filterPrice(rawData, tags, true);
    return data;
  }

  // Computed: 升级VIP数据
  get upVipData(): Merchandise | undefined {
    const oldVip = this.merchandises?.find((item: any) => item?.id === this.orderData?.productId);
    if (!oldVip) return undefined;

    const { quantity, unit } = oldVip;
    return this.merchandises?.find(
      (item: any) => item?.quantity === quantity && item?.unit === unit && item?.category === 'supreme'
    );
  }

  // Computed: 升级价格
  get upPrice(): number {
    if (!this.upVipData?.quantity) return 0;

    const filterArr = this.vipData?.filter(
      (item: any) =>
        item?.quantity === this.upVipData?.quantity &&
        item?.vipType === this.currentVip &&
        item?.unit === this.upVipData?.unit
    );
    return (filterArr?.[0]?.price || 0) - this.offsetAmount;
  }

  // Computed: 价格数据源选择
  get priceSource(): FilteredPrice[] {
    const hasPromotion =
      this.activePayCard?.discountable &&
      (this.merchandiseStore[CategoryToCurVip[this.currentVip]]?.[0]?.promotionStockKeepUnits?.length > 0 ||
        this.merchandiseStore[CategoryToCurVip[this.currentVip]]?.find(
          (item) => item.discountStockKeepUnits?.length > 0
        ));

    return hasPromotion ? this.promotionVipData : this.vipData;
  }

  // 从URL中删除指定参数
  private removeUrlParam(paramName: string) {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    url.searchParams.delete(paramName);

    // 使用 replaceState 更新URL，不添加历史记录
    window.history.replaceState({}, '', url.toString());
  }

  // 获取会员商品信息
  async getMerchandise(currencyCode: string) {
    try {
      const res = await getWithAuth(`/web/membership/users/me/merchandises?currencyCode=${currencyCode}`);
      runInAction(() => {
        this.merchandises = res.data.merchandises;
        this.timeUnit =
          this.merchandises?.filter((item: Merchandise) => item.unit === 'days')?.length > 0 ? '/w' : '/m';
        // 将商品按会员类型分类，方便做价格展示
        this.merchandiseStore = {};
        const urlParams = new URLSearchParams(window.location.search);
        const pid = urlParams.get('pid');
        if (pid) {
          const targetMerchandise = res.data.merchandises.find((item: any) => item.id === pid);
          if (targetMerchandise) {
            // Set currentVip based on category
            const vipType = vipObj[targetMerchandise.category];
            if (typeof vipType === 'number') {
              this.currentVip = vipType;
            }
            // Set currentMonth based on quantity
            const monthIndex = TimePeriods.findIndex((item) => item.value === targetMerchandise.quantity);
            if (monthIndex !== -1) {
              this.currentMonth = monthIndex;
            }
          }
          // 使用完pid参数后从URL中删除
          this.removeUrlParam('pid');
        }
        res.data.merchandises.forEach((item: Merchandise) => {
          const { category } = item;
          if (this.merchandiseStore[category]) {
            this.merchandiseStore[category].push(item);
          } else {
            this.merchandiseStore[category] = [item];
          }
        });
      });
    } catch {
      /* empty */
    }
  }

  // 获取最近一笔订单信息
  async getOrder() {
    try {
      const res = await getWithAuth(
        '/web/payment/users/me/orders?action=latest&productType=seeWhoLikedMe&orderStatus=success'
      );
      runInAction(() => {
        if (res?.data?.orders?.length > 0) {
          const orderData = res.data.orders[0];
          const { duration = 0, createdTime = 0 } = orderData;
          if (createdTime + duration * 1000 > new Date().getTime()) {
            // 会员未过期，可以升级(Premium → Supreme)
            this.orderData = res.data.orders[0];
          }
        }
      });
    } catch {
      /* empty */
    }
  }

  // 获取支付渠道信息(这是订阅型的)
  async getCardData(currencyCode: string) {
    try {
      const res = await getWithAuth(
        `/web/payment/payment-channels?currencyCode=${currencyCode}&subType=autoRenewable&platform=web`
      );
      if (Array.isArray(res?.data?.paymentChannels)) {
        let { paymentChannels } = res.data;

        // 检测当前设备是否为 Android，如果是则过滤掉 Apple Pay 渠道
        // 使用与 useAndroid hook 相同的逻辑
        const u = window.navigator.userAgent;
        const isIOS = /Macintosh|Mac OS X/i.test(u) || /iPhone|iPad|iPod/i.test(navigator.userAgent);
        const isAndroid = !isIOS;

        if (isAndroid) {
          paymentChannels = paymentChannels.filter(
            (channel: PaymentChannel) => !ApplePayMethods.includes(channel.paymentMethod)
          );
        }

        this.cardData = paymentChannels;
        const urlParams = new URLSearchParams(window.location.search);
        const channelId = urlParams.get('channel_id');
        if (channelId) {
          const index = this.cardData.findIndex((item: PaymentChannel) => item.id === channelId);
          if (index !== -1) {
            // 初始化时直接设置，不需要重新获取升级信息
            this.activeChannelIndex = index;
          }
        }
      } else {
        this.cardData = [];
      }
    } catch {
      this.cardData = [];
    }
  }

  initMockData() {
    this.merchandises = mockData2.data.merchandises;
    setTimeout(() => {
      mockData2.data.merchandises.forEach((item: Merchandise) => {
        const { category } = item;
        if (this.merchandiseStore[category]) {
          this.merchandiseStore[category].push(item);
        } else {
          this.merchandiseStore[category] = [item];
        }
      });
    }, 2000);

    // setTimeout(() => {
    //   this.cardData = mockData3.data.paymentChannels;
    // }, 1000);

    this.orderData = mockData4.data.orders[0];
  }

  async initData(currencyCode?: string) {
    // 等待所有初始化请求完成
    await Promise.all([
      this.getMerchandise(currencyCode || 'USD'),
      this.getOrder(),
      this.getCardData(currencyCode || 'USD'),
    ]);

    // 尝试获取升级信息
    await this.checkUpgradeAvailability(currencyCode);

    // 标记初始化完成
    runInAction(() => {
      this.initDone = true;
    });
  }

  // 检查升级可用性
  private async checkUpgradeAvailability(currencyCode: string) {
    // 如果没有订单数据，直接返回
    if (!this.orderData?.id) return;

    // 获取升级商品信息
    const upgradeInfo = this.getUpgradeInfo();
    if (!upgradeInfo) return;

    // 生成缓存key
    const cacheKey = `${this.orderData.id}_${upgradeInfo.itemId}_${currencyCode}`;

    // 检查缓存中是否已有接口数据
    if (this.upgradeApiCache[cacheKey]) {
      const cachedRes = this.upgradeApiCache[cacheKey];
      // 从缓存的接口数据中提取状态
      if (Array.isArray(cachedRes?.data?.upgradeOrders) && cachedRes.data.upgradeOrders.length > 0) {
        const upgradeOrder = cachedRes.data.upgradeOrders[0];
        runInAction(() => {
          this.canUp = true;
          this.offsetAmount = upgradeOrder?.offsetAmount || 0;
          this.orderId = upgradeOrder?.orderId;
        });
      } else {
        runInAction(() => {
          this.canUp = false;
          this.offsetAmount = 0;
          this.orderId = undefined;
        });
      }
      return;
    }

    try {
      const res = await getWithAuth(
        `/web/payment/users/me/orders/${this.orderData.id}/upgrade?upgradeItemId=${upgradeInfo.itemId}&currencyCode=${currencyCode}`
      );
      // const res = { meta: { code: 200, message: 'OK' }, data: { upgradeOrders: [{ orderId: '1903736', upgradeAmount: 35.03, offsetAmount: 24.956411290322578 }] } };

      // 缓存接口返回的原始数据
      runInAction(() => {
        this.upgradeApiCache[cacheKey] = res;
      });

      // 从接口数据中提取状态
      if (Array.isArray(res?.data?.upgradeOrders) && res.data.upgradeOrders.length > 0) {
        const upgradeOrder = res.data.upgradeOrders[0];
        runInAction(() => {
          this.canUp = true;
          this.offsetAmount = upgradeOrder?.offsetAmount || 0;
          this.orderId = upgradeOrder?.orderId;
        });
      } else {
        runInAction(() => {
          this.canUp = false;
          this.offsetAmount = 0;
          this.orderId = undefined;
        });
      }
    } catch (error) {
      // 升级请求失败时静默处理
      console.warn('Failed to check upgrade availability:', error);
    }
  }

  // 获取升级商品信息
  private getUpgradeInfo() {
    const oldVip = this.merchandises?.find((item) => item?.id === this.orderData?.productId);
    if (!oldVip) return null;

    const upVipData = this.merchandises?.find(
      (item) => item?.quantity === oldVip.quantity && item?.unit === oldVip.unit && item?.category === 'supreme'
    );

    if (!upVipData) return null;

    const { promotionStockKeepUnits, defaultStockKeepUnits, discountStockKeepUnits } = upVipData;
    const activePayCard = this.cardData[this.activeChannelIndex];

    // 选择价格信息
    const priceInfo =
      promotionStockKeepUnits?.length > 0 && activePayCard?.discountable
        ? promotionStockKeepUnits || discountStockKeepUnits
        : defaultStockKeepUnits;

    return priceInfo?.length > 0 ? { itemId: priceInfo[0].id } : null;
  }

  // Action: 设置支付渠道索引并重新获取升级信息
  setActiveChannelIndex = async (index: number, currencyCode: string = 'USD') => {
    this.activeChannelIndex = index;

    // 如果已经初始化完成且有订单数据，重新获取升级信息
    if (this.initDone && this.orderData?.id) {
      await this.checkUpgradeAvailability(currencyCode);
    }
  };
}
const rootStore = new Store();
const context = createContext(rootStore);
const useStore = () => useContext(context);

export default useStore;
